import React, { useRef, useState, useEffect } from "react";
import { RotateCw } from "lucide-react";

/**
 * Interactive design overlay: draggable + resizable + rotatable.
 * value: { top, left, width, height, rotation } (percent, degrees)
 * lockAspect: if true, resize keeps width/height ratio (proportional).
 */
export default function DraggableDesign({
  src,
  value,
  onChange,
  editable = true,
  blendMode = "multiply",
  lockAspect = false,
}) {
  const containerRef = useRef(null);
  const [action, setAction] = useState(null); // 'move' | 'resize' | 'rotate' | null
  const startRef = useRef(null);

  useEffect(() => {
    if (!action) return;
    const parentEl = containerRef.current?.parentElement;
    const parentRect = parentEl?.getBoundingClientRect();

    const onMove = (e) => {
      if (!parentRect) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const dxPct = ((clientX - startRef.current.x) / parentRect.width) * 100;
      const dyPct = ((clientY - startRef.current.y) / parentRect.height) * 100;

      if (action === "move") {
        const nextLeft = Math.max(0, Math.min(100 - startRef.current.width, startRef.current.left + dxPct));
        const nextTop = Math.max(0, Math.min(100 - startRef.current.height, startRef.current.top + dyPct));
        onChange({ ...value, left: nextLeft, top: nextTop });
      } else if (action === "resize") {
        if (startRef.current.lockAspect) {
          const ratio = startRef.current.height / startRef.current.width;
          const nextWidth = Math.max(8, Math.min(100 - startRef.current.left, startRef.current.width + dxPct));
          const nextHeight = Math.max(8, Math.min(100 - startRef.current.top, nextWidth * ratio));
          onChange({ ...value, width: nextWidth, height: nextHeight });
        } else {
          const nextWidth = Math.max(8, Math.min(100 - startRef.current.left, startRef.current.width + dxPct));
          const nextHeight = Math.max(8, Math.min(100 - startRef.current.top, startRef.current.height + dyPct));
          onChange({ ...value, width: nextWidth, height: nextHeight });
        }
      } else if (action === "rotate") {
        // rotation from center of overlay
        const cx = startRef.current.centerX;
        const cy = startRef.current.centerY;
        const angle = (Math.atan2(clientY - cy, clientX - cx) * 180) / Math.PI + 90;
        onChange({ ...value, rotation: Math.round(angle) });
      }
    };
    const onUp = () => setAction(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [action, value, onChange]);

  const beginAction = (kind) => (e) => {
    if (!editable) return;
    e.preventDefault();
    e.stopPropagation();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const overlayRect = containerRef.current?.getBoundingClientRect();
    startRef.current = {
      x: clientX,
      y: clientY,
      left: value.left,
      top: value.top,
      width: value.width,
      height: value.height,
      rotation: value.rotation || 0,
      lockAspect,
      centerX: overlayRect ? overlayRect.left + overlayRect.width / 2 : 0,
      centerY: overlayRect ? overlayRect.top + overlayRect.height / 2 : 0,
    };
    setAction(kind);
  };

  const rotation = value.rotation || 0;

  return (
    <div
      ref={containerRef}
      className={`absolute ${editable ? "cursor-move" : ""}`}
      style={{
        top: `${value.top}%`,
        left: `${value.left}%`,
        width: `${value.width}%`,
        height: `${value.height}%`,
        outline: editable ? "1px dashed rgba(255, 59, 48, 0.7)" : "none",
        outlineOffset: 2,
        transform: `rotate(${rotation}deg)`,
        transformOrigin: "center",
      }}
      onMouseDown={beginAction("move")}
      onTouchStart={beginAction("move")}
      data-testid="draggable-design"
    >
      <img
        src={src}
        alt="diseño"
        draggable={false}
        className="w-full h-full object-contain select-none pointer-events-none"
        style={{ mixBlendMode: blendMode }}
      />
      {editable && (
        <>
          {/* Resize handle: bottom-right */}
          <div
            className="absolute -right-1.5 -bottom-1.5 w-4 h-4 bg-[#FF3B30] border-2 border-white cursor-nwse-resize"
            onMouseDown={beginAction("resize")}
            onTouchStart={beginAction("resize")}
            data-testid="draggable-resize-handle"
            title={lockAspect ? "Redimensionar (proporcional)" : "Redimensionar libre"}
          />
          {/* Rotate handle: top-center, offset above the overlay */}
          <div
            className="absolute left-1/2 -translate-x-1/2 -top-8 flex flex-col items-center"
            style={{ pointerEvents: "none" }}
          >
            <div className="w-px h-6 bg-[#FF3B30]" />
            <button
              type="button"
              className="w-6 h-6 rounded-full bg-white border-2 border-[#FF3B30] cursor-grab active:cursor-grabbing flex items-center justify-center"
              style={{ pointerEvents: "auto" }}
              onMouseDown={beginAction("rotate")}
              onTouchStart={beginAction("rotate")}
              data-testid="draggable-rotate-handle"
              title="Rotar"
            >
              <RotateCw className="w-3 h-3 text-[#FF3B30]" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
